import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const HowItWorksPage = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      number: "01",
      title: "Upload Your Audio",
      description: "Drag and drop your audio files or record directly. We support WAV, MP3, FLAC, and 20+ formats.",
      details: "Start with any audio file. Our platform accepts all professional audio formats and handles large batch uploads seamlessly."
    },
    {
      number: "02",
      title: "AI Analysis",
      description: "Our AI analyzes patterns, quality issues, and optimization opportunities in real-time.",
      details: "Advanced ML algorithms scan your audio for noise, tone, clarity, and suggest personalized enhancements."
    },
    {
      number: "03",
      title: "Smart Processing",
      description: "Apply AI-powered enhancements, generate content, or automate workflow tasks intelligently.",
      details: "Choose from pre-built templates or create custom workflows. Apply effects, enhancement, and transformations in seconds."
    },
    {
      number: "04",
      title: "Export & Share",
      description: "Download your optimized audio in any format or share directly to your favorite platforms.",
      details: "Export in 20+ formats, share directly to YouTube, Spotify, or integrate via API. One-click publishing."
    },
  ];

  return (
    <div className="min-h-screen bg-dark-base pt-24">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-dark-base/95 backdrop-blur border-b border-neon-orange/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-white font-heading font-bold text-xl hover:text-neon-orange transition-colors">
            ← Back to Home
          </Link>
          <h1 className="text-neon-orange font-heading font-bold">How It Works</h1>
          <div className="w-24"></div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Your Audio Journey in <span className="text-neon-orange">4 Simple Steps</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            From upload to export, experience the future of audio processing.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className={`p-8 rounded-2xl border-2 transition-all cursor-pointer ${
                activeStep === index
                  ? 'border-neon-orange bg-neon-orange/10'
                  : 'border-neon-orange/20 bg-dark-deeper/50 hover:border-neon-orange/40'
              }`}
              onClick={() => setActiveStep(index)}
              whileHover={{ scale: index === activeStep ? 1 : 1.02 }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="text-4xl font-bold text-neon-orange">{step.number}</div>
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{step.description}</p>
                </div>
              </div>
              
              {activeStep === index && (
                <motion.div
                  className="mt-4 pt-4 border-t border-neon-orange/20"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-gray-300 text-sm leading-relaxed">{step.details}</p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Timeline Visualization */}
        <motion.div
          className="mb-16 p-8 bg-dark-deeper rounded-2xl border border-neon-orange/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-white font-heading font-bold mb-6">Process Flow</h3>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {steps.map((step, i) => (
              <div key={i} className="flex items-center gap-4 flex-1">
                <div className="w-12 h-12 rounded-full bg-neon-orange/20 border-2 border-neon-orange flex items-center justify-center flex-shrink-0">
                  <span className="text-neon-orange font-bold">{i + 1}</span>
                </div>
                <div className="hidden md:block text-sm text-gray-400">{step.title}</div>
                {i < steps.length - 1 && (
                  <div className="hidden md:block h-1 flex-1 bg-gradient-to-r from-neon-orange/50 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Link 
            to="/veritonetech-ai"
            className="inline-block px-8 py-4 bg-neon-orange text-dark-base font-bold rounded-full hover:shadow-lg hover:shadow-neon-orange/50 transition-all"
          >
            Start Your Journey Now
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default HowItWorksPage;
